<template>
	<div class="slider">
		<swiper-root
			v-bind="{ ...swiperProps }"
			@before-init="onBeforeInit"
			@real-index-change="onSlideChange"
		>
			<div class="swiper-pagination swiper-pagination-bullets" :class="additionalClasses" />

			<swiper-slide v-for="(item, index) in data" :key="index" class="swiper-slide">
				<slot name="slide" v-bind="{ data: item }" />
			</swiper-slide>
		</swiper-root>
	</div>
</template>

<script lang="ts">
import 'swiper/scss';
import 'swiper/scss/pagination';

import Swiper, { Autoplay, Keyboard, Pagination } from 'swiper';
import { Swiper as SwiperRoot, SwiperSlide } from 'swiper/vue';
import { computed, defineComponent, PropType, shallowRef } from 'vue';

export type SwiperProps = InstanceType<typeof SwiperRoot>['$props'];

export type PaginationAlign =
	| 'top-right'
	| 'top-center'
	| 'top-right'
	| 'bottom-left'
	| 'bottom-center'
	| 'bottom-right';

export default defineComponent({
	components: {
		SwiperRoot,
		SwiperSlide,
	},

	props: {
		data: {
			type: Array,
			required: true,
		},

		paginationAlign: {
			type: String as PropType<PaginationAlign>,
			default: null,
		},

		config: {
			type: Object as PropType<SwiperProps>,
			default: () => ({}),
		},
	},

	emits: ['change'],

	setup(props) {
		const swiper = shallowRef<Swiper>();
		const swiperProps = computed<SwiperProps>(() => {
			return {
				watchOverflow: true,
				grabCursor: true,
				keyboard: {
					enabled: true,
				},
				pagination: {
					el: '.swiper-pagination',
					clickable: true,
					type: 'bullets',

					renderBullet: (index, className) => `<span class="${className}"></span>`,
				},
				modules: [Pagination, Autoplay, Keyboard],

				...(props.config as SwiperProps),
			};
		});

		return {
			swiper,
			swiperProps,

			onBeforeInit: (_swiper: Swiper) => {
				swiper.value = _swiper;
			},
		};
	},

	computed: {
		additionalClasses(): Array<string> {
			const classes = new Array<string>();

			if (this.paginationAlign) classes.push(`swiper-pagination--align-${this.paginationAlign}`);

			return classes;
		},
	},

	methods: {
		onSlideChange() {
			const active: number = this.swiper?.realIndex ?? 0;

			this.$emit('change', active);
		},

		async onResize() {
			if (!this.swiper) {
				return;
			}

			if (this.swiper.isBeginning && this.swiper.isEnd && this.$refs.swiper) {
				(this.$refs.swiper as Element).removeAttribute('style');
			}
		},
	},
});
</script>

<style lang="scss">
.slider {
	height: auto;

	.swiper {
		.swiper-pagination {
			width: auto;
			height: 2.4rem;
			margin: 0;

			@include max-md-and-portrait {
				right: 0;
			}

			&--align-top-right {
				top: 0;
				right: -0.5rem;
				left: auto;
			}

			&--align-bottom-center {
				top: auto;
				bottom: 1rem;
				left: 50%;
				transform: translateX(-50%);
			}
		}

		.swiper-pagination-bullet {
			width: 1.2rem;
			height: 1.2rem;
			margin: 0 0.6rem;
			font-size: 12px;
			transition: 0.2s ease;
			opacity: 1;
			border: 1px solid $color-dark;
			border-radius: 50%;
			outline: none;
			background: transparent;

			@include max-md-and-portrait {
				margin: 0 0.7rem;
			}

			&:only-child {
				visibility: hidden;
				pointer-events: none;
			}
		}

		.swiper-pagination-bullet-active {
			width: 4.2rem;
			height: 1.2rem;
			border-radius: 0.8rem;
			background-color: $color-dark;

			@include max-md-and-portrait {
				width: 4.2rem;
				height: 1.2rem;
			}
		}
	}
}
</style>
